import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard, Inbox, Wrench, DollarSign, PlusSquare,
  Settings as SettingsIcon, Search, Star, HelpCircle, LogOut, Sparkles, Menu, X,
  Activity, Users, BarChart3, ShieldCheck, Image as ImageIcon, FileText, LayoutTemplate, Link2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const OWNER_NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "admin-nav-dashboard" },
  { to: "/admin/requests", label: "Estimate Requests", icon: Inbox, testid: "admin-nav-requests" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, testid: "admin-nav-analytics" },
  { to: "/admin/services", label: "Services", icon: Wrench, testid: "admin-nav-services" },
  { to: "/admin/pricing", label: "Pricing", icon: DollarSign, testid: "admin-nav-pricing" },
  { to: "/admin/addons", label: "Add-Ons", icon: PlusSquare, testid: "admin-nav-addons" },
  { to: "/admin/reviews", label: "Reviews", icon: Star, testid: "admin-nav-reviews" },
  { to: "/admin/faqs", label: "FAQs", icon: HelpCircle, testid: "admin-nav-faqs" },
  { to: "/admin/homepage-images", label: "Homepage Images", icon: ImageIcon, testid: "admin-nav-homepage-images" },
  { to: "/admin/about", label: "About Page", icon: FileText, testid: "admin-nav-about" },
  { to: "/admin/landing-pages", label: "Landing Pages", icon: LayoutTemplate, testid: "admin-nav-landing-pages" },
  { to: "/admin/internal-links", label: "Internal Links", icon: Link2, testid: "admin-nav-internal-links" },
  { to: "/admin/settings", label: "Website Settings", icon: SettingsIcon, testid: "admin-nav-settings" },
  { to: "/admin/seo", label: "SEO Settings", icon: Search, testid: "admin-nav-seo" },
  { to: "/admin/users", label: "Users", icon: Users, testid: "admin-nav-users" },
  { to: "/admin/activity", label: "Activity Log", icon: Activity, testid: "admin-nav-activity" },
  { to: "/admin/dev", label: "Developer Dashboard", icon: ShieldCheck, testid: "admin-nav-dev" },
];

const DEV_NAV = [
  { to: "/admin/dev", label: "Developer Dashboard", icon: ShieldCheck, end: true, testid: "admin-nav-dev" },
];

export default function AdminLayout() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const items = user?.role === "owner" ? OWNER_NAV : DEV_NAV;

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex" data-testid="admin-layout">
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-72 bg-white border-r border-slate-100 transform transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="h-16 flex items-center justify-between px-5 border-b border-slate-100">
          <Link to={user?.role === "developer" ? "/admin/dev" : "/admin"} className="flex items-center gap-2" data-testid="admin-logo">
            <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1E3A8A] to-[#8B5CF6] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={2.4} />
            </span>
            <span className="font-display font-bold text-[#0F172A]">P &amp; A Admin</span>
          </Link>
          <button className="lg:hidden p-1.5 rounded" onClick={() => setOpen(false)} aria-label="Close menu"><X className="w-5 h-5" /></button>
        </div>
        <nav className="p-3 space-y-1 overflow-y-auto" style={{ maxHeight: "calc(100vh - 64px)" }}>
          {items.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to} to={to} end={end} onClick={() => setOpen(false)}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-[#1E3A8A] text-white" : "text-[#0F172A] hover:bg-slate-50"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}
          <button
            onClick={handleLogout}
            data-testid="admin-logout"
            className="w-full mt-4 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[#0F172A] hover:bg-red-50 hover:text-red-700 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </nav>
        <div className="absolute bottom-3 left-3 right-3 text-xs text-slate-400 px-2" data-testid="admin-user-info">
          <div>{user?.email || "—"}</div>
          <div className="text-[10px] uppercase tracking-wider mt-0.5 text-[#1E3A8A] font-semibold">{user?.role}</div>
        </div>
      </aside>

      <div className="flex-1 min-w-0 lg:ml-0">
        <header className="h-16 bg-white border-b border-slate-100 px-4 lg:px-8 flex items-center justify-between sticky top-0 z-30">
          <button className="lg:hidden p-2 -ml-2" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="admin-mobile-toggle"><Menu className="w-5 h-5" /></button>
          <h1 className="font-display font-semibold text-[#0F172A] text-lg">Admin</h1>
          <Link to="/" className="text-sm text-[#1E3A8A] hover:underline" data-testid="admin-view-site">View site →</Link>
        </header>
        <main className="p-4 lg:p-8 max-w-6xl">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
